// Armor and damage control. Incoming shells resolve against the armor
// scheme (belt / deck / barbette / superstructure); penetrations start
// fires and flooding in real compartments, which repair parties (or you,
// with an extinguisher and some elbow grease) fight down. Flooding lists
// the ship, slows her, and — enough of it — sinks her. A magazine fire is
// a countdown to catastrophe unless somebody floods the magazine.

import * as THREE from "three";
import { WATERLINE_Y, CENTER_Z } from "./const.js";
import { hitFlash, fireSmoke } from "./effects.js";

const DAMAGEABLE = [
  "engine", "fireroom", "plot", "magazine", "cic", "bridge", "mess", "berthing",
  "sickbay", "dccentral", "wardroom", "radio", "steering", "stores", "galley", "gedunk",
];

export class Damage {
  constructor(G) {
    this.G = G;
    this.comps = [];
    for (const key of DAMAGEABLE) {
      const r = G.ship.rooms.find((r) => r.key === key);
      if (!r) continue;
      this.comps.push({
        key, name: r.name, room: r,
        center: new THREE.Vector3((r.x0 + r.x1) / 2, r.walk, (r.z0 + r.z1) / 2),
        fire: 0, flood: 0, wrecked: false, party: null,
      });
    }
    this.radarAlive = true;
    this.plotAlive = true;
    this.afloat = true;
    this.magazineFuse = null; // seconds until magazine explosion
    this.magazineFlooded = false;
    this.floodTotal = 0;
    this.hitsTaken = 0;
    this.shellsShrugged = 0;
    this.torpedoFloods = 0;
    this.counterflood = 0;
    this._smokeAcc = 0;
  }

  comp(key) { return this.comps.find((c) => c.key === key); }

  // --- incoming shell resolution -----------------------------------------
  // `at` is the world-space splash point of an enemy shell.
  resolveIncoming(at, shell) {
    const G = this.G;
    // to ship-local
    const local = G.shipInner.worldToLocal(at.clone());
    const inHullX = local.x > 1 && local.x < 271;
    const halfB = 17.5; // generous
    const inHullZ = Math.abs(local.z - CENTER_Z - 0.5) < halfB;
    if (!inHullX || !inHullZ) {
      // near miss: splash alongside, maybe splinters
      const d = Math.hypot(Math.max(0, Math.abs(local.x - 136) - 135), Math.abs(local.z - 17.5));
      const { shellSplash } = G.fx;
      shellSplash(G.worldFx, at, true);
      if (d < 26) {
        G.audio.splash(true);
        G.hud.log("NEAR MISS — SPLINTERS ON DECK", "bad");
        G.shake(0.35);
      } else if (d < 90) G.audio.splash(true);
      return;
    }

    // It's aboard. Where did it strike?
    this.hitsTaken++;
    G.shake(1);
    G.audio.hitTaken();

    // Field artillery (the shore batteries' 130 mm): a splinter threat to the
    // topside spaces, nothing more — no belt, no barbettes, no flooding.
    if (shell?.light) {
      const impactL = G.shipInner.localToWorld(new THREE.Vector3(local.x, Math.max(14, local.y + 15), local.z));
      hitFlash(G.worldFx, impactL);
      if (Math.random() < 0.68) {
        this.shellsShrugged++;
        G.audio.ricochet();
        G.hud.log("HIT — SPLINTER DAMAGE TOPSIDE. ARMOR UNIMPRESSED", "ok");
        return;
      }
      const topside = this.comps.filter((c) => c.room.walk >= 14)
        .sort((a, b) => Math.abs(a.center.x - local.x) - Math.abs(b.center.x - local.x));
      const c = topside[0];
      if (!c) return;
      this.applyCompartmentDamage(c, false, true);
      return;
    }

    const nearTurret = G.ship.turrets.find((t) => Math.abs(local.x - t.cx) < 9 && Math.abs(local.z - t.cz) < 9);
    const plunging = Math.random() < 0.55; // long-range fire comes down steep
    let penetrated, where;

    if (nearTurret) {
      where = `TURRET ${nearTurret.id.slice(1)} / BARBETTE`;
      penetrated = Math.random() < 0.10; // 17" faceplates shrug almost everything
      if (penetrated) {
        nearTurret.disabled = true;
        G.hud.log(`${where} PENETRATED — TURRET OUT OF ACTION`, "bad");
      }
    } else if (!plunging) {
      where = "MAIN BELT";
      penetrated = Math.random() < 0.18;
    } else {
      where = "WEATHER DECK";
      penetrated = Math.random() < 0.45; // deck hits over superstructure mostly get through somewhere
    }

    const impact = G.shipInner.localToWorld(new THREE.Vector3(local.x, Math.max(13, local.y + 14), local.z));
    hitFlash(G.worldFx, impact);

    if (!penetrated) {
      this.shellsShrugged++;
      G.audio.ricochet();
      G.hud.log(`HIT ${where} — ARMOR HELD. SHELL BROKE UP`, "ok");
      return;
    }

    // pick the compartment that eats it: nearest by x, weighted random
    const sorted = [...this.comps].sort((a, b) =>
      Math.abs(a.center.x - local.x) - Math.abs(b.center.x - local.x));
    const c = sorted[Math.random() < 0.7 ? 0 : 1 + ((Math.random() * 2) | 0)] || sorted[0];
    this.applyCompartmentDamage(c, plunging);
  }

  applyCompartmentDamage(c, plunging, light = false) {
    const G = this.G;
    const belowWater = c.room.walk < WATERLINE_Y;
    c.fire = Math.min(1, c.fire + (light ? 0.22 + Math.random() * 0.2 : 0.4 + Math.random() * 0.4));
    if (!light && (belowWater || (!plunging && Math.random() < 0.4))) c.flood = Math.min(1, c.flood + 0.3 + Math.random() * 0.4);
    G.hud.log(light
      ? `SHELL HIT — ${c.name}, FIRE`
      : `PENETRATION — ${c.name}${c.flood > 0 ? ", FLOODING" : ""}, FIRE`, "bad");

    // system effects
    if (c.key === "magazine" && !this.magazineFlooded && this.magazineFuse === null) {
      this.magazineFuse = 75;
      G.hud.log("FIRE IN THE 16-IN MAGAZINE — FLOOD THE MAGAZINE! (DC CENTRAL)", "bad");
      G.audio.klaxon(5);
    }
    if (c.key === "plot") { this.plotAlive = false; G.hud.log("MAIN BATTERY PLOT WRECKED — SOLUTION DEGRADED", "bad"); }
    if (c.key === "cic") { this.radarAlive = false; G.hud.log("CIC HIT — SG RADAR OFF THE AIR", "bad"); }
    if (c.key === "steering") { G.shipState.rudderJammed = true; G.hud.log("STEERING GEAR HIT — RUDDER JAMMED", "bad"); }
    if (c.key === "engine" || c.key === "fireroom") G.hud.log("MACHINERY SPACE HIT — LOSING STEAM", "bad");

    G.crew.dispatchParty(c);
  }

  // --- update --------------------------------------------------------------
  update(dt) {
    const G = this.G;
    let flood = this.counterflood * 0.5;
    let fireTotal = 0;

    for (const c of this.comps) {
      // fires spread slowly if unattended
      if (c.fire > 0) {
        c.fire = Math.min(1.2, c.fire + dt * 0.006);
        fireTotal += c.fire;
        // fire visuals from the compartment's topside vent
        this._smokeAcc += dt * c.fire;
        if (this._smokeAcc > 0.25) {
          this._smokeAcc = 0;
          const vent = new THREE.Vector3(c.center.x + (Math.random() - 0.5) * 4, c.room.walk >= 14 ? c.room.walk + 2 : 15, c.center.z);
          fireSmoke(G.shipFx, vent, Math.min(1.5, c.fire + 0.4));
        }
        if (Math.random() < dt * 2 && this.playerNear(c, 20)) G.audio.fireCrackle();
        // fires below reach magazines eventually
        if (c.key === "magazine" && this.magazineFuse === null && !this.magazineFlooded && c.fire > 0.7) {
          this.magazineFuse = 75;
          G.hud.log("MAGAZINE TEMPERATURE RISING — FLOOD THE MAGAZINE!", "bad");
        }
      }
      if (c.flood > 0) {
        // progressive flooding below the waterline
        if (c.room.walk < WATERLINE_Y) c.flood = Math.min(1.3, c.flood + dt * 0.004);
        flood += c.flood * (c.room.walk < WATERLINE_Y ? 1 : 0.4);
      }
      // party works the casualty
      if (c.party && c.party.working) {
        c.fire = Math.max(0, c.fire - dt * 0.05);
        c.flood = Math.max(0, c.flood - dt * 0.028);
        if (c.fire === 0 && c.flood === 0) {
          G.hud.log(`${c.name}: CASUALTY SECURED — REPAIR ${c.party.id} RETURNING`, "ok");
          G.crew.releaseParty(c);
        }
      }
      // player fighting the fire in person
      if (c.fire > 0 && G.playerDousing && this.playerNear(c, 4)) {
        c.fire = Math.max(0, c.fire - dt * 0.12);
        if (c.fire === 0) G.hud.log(`${c.name}: FIRE OUT — GOOD WORK, SAILOR`, "ok");
      }
    }

    this.floodTotal = flood;
    // list toward the flooded side would need per-side bookkeeping; the toy
    // rolls with total flooding plus the player's counterflood trim.
    const targetList = Math.min(0.14, flood * 0.014) - this.counterflood * 0.01;
    G.shipState.list += (targetList - G.shipState.list) * dt * 0.2;
    G.shipState.trim += (Math.min(0.02, flood * 0.002) - G.shipState.trim) * dt * 0.1;

    // machinery casualties cap speed
    const eng = this.comp("engine"), fr = this.comp("fireroom");
    const engDam = Math.max(eng ? eng.fire + eng.flood : 0, fr ? fr.fire + fr.flood : 0);
    G.shipState.speedCap = Math.max(4, 16.5 * (1 - engDam * 0.6));

    // magazine fuse
    if (this.magazineFuse !== null && !this.magazineFlooded) {
      this.magazineFuse -= dt;
      if (this.magazineFuse <= 0) this.catastrophe();
    }

    // founder?
    if (flood > 6 && this.afloat) this.founder("PROGRESSIVE FLOODING");
  }

  playerNear(c, r) {
    const p = this.G.player.pos;
    return Math.abs(p.x - c.center.x) < (c.room.x1 - c.room.x0) / 2 + r &&
           Math.abs(p.y - c.room.walk) < 4 &&
           Math.abs(p.z - c.center.z) < (c.room.z1 - c.room.z0) / 2 + r;
  }

  // A torpedo under the belt. localX is the hit's ship-local frame position.
  // The Iowas carried a deep torpedo defense system — sometimes it holds.
  torpedoHit(localX) {
    const G = this.G;
    this.hitsTaken++;
    if (Math.random() < 0.22) {
      this.shellsShrugged++;
      G.hud.log("TORPEDO HIT — THE TORPEDO DEFENSE SYSTEM HELD. WHIPPED HARD, TAKING SPRAY, STILL DRY", "ok");
      G.shipState.recoil = 0.05;
      return;
    }
    // major flooding in the two below-waterline compartments nearest the hit
    this.torpedoFloods++;
    const below = this.comps.filter((c) => c.room.walk < WATERLINE_Y)
      .sort((a, b) => Math.abs(a.center.x - localX) - Math.abs(b.center.x - localX));
    const struck = below.slice(0, 2);
    for (const c of struck) {
      c.flood = Math.min(1.3, c.flood + 0.75 + Math.random() * 0.35);
      c.fire = Math.min(1, c.fire + 0.15);
      G.crew.dispatchParty(c);
    }
    const where = localX > 180 ? "FORWARD" : localX < 90 ? "AFT" : "AMIDSHIPS";
    G.hud.log(`TORPEDO HIT ${where} — MAJOR FLOODING${struck[0] ? ": " + struck[0].name : ""}`, "bad");
    if (struck.some((c) => c.key === "engine" || c.key === "fireroom")) {
      G.hud.log("MACHINERY SPACES FLOODING — LOSING STEAM FAST", "bad");
    }
    if (!G.gq) G.soundGQ(true, true);
  }

  floodMagazine() {
    if (this.magazineFlooded) return;
    this.magazineFlooded = true;
    this.magazineFuse = null;
    const c = this.comp("magazine");
    if (c) { c.fire = 0; c.flood = Math.max(c.flood, 0.8); }
    const t2 = this.G.ship.turrets[1];
    if (t2) t2.disabled = true;
    this.G.hud.log("16-IN MAGAZINE FLOODED — TURRET II WITHOUT AMMUNITION, SHIP SAFE", "ok");
  }

  catastrophe() {
    this.G.hud.log("MAGAZINE EXPLOSION", "bad");
    this.afloat = false;
    this.G.gameOver(false, "The forward magazine went up. Nobody swims away from that.");
  }
  founder(why) {
    this.afloat = false;
    this.G.gameOver(false, `${why} — the ship is going down.`);
  }
}
